/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Apr 09 2024
 **/

import { useCallback, useState, useRef, useEffect } from "react";
import { Upload as TusUpload } from "tus-js-client";

import { Utility } from "@/frameworks/libraries/utilities";

import { useFileManager, useUploadTus } from "@/frameworks/core/hooks";
import useOnlineMonitor from "@/frameworks/core/hooks/useServiceInitialize/private/useOnlineMonitor";
import { checkHasLink } from "../utils";
import { ChatEditorRefType } from "../../ChatEditor";
import useUploadChat from "./useUploadChat";
import { useToast } from "@/frameworks/designStuff/components";
import { _T } from "@rfice/language";
import { RestAPIError } from "@/frameworks/libraries/restservice";

import { MessageInValidateFilename } from "@/frameworks/core/utils";

type ChatFile = { file: File; displaySize: string; isLoading: boolean; id: string; tusID: string; url?: string };
type ChatFileFormat = (
	| ChatServices.Format2.Elements.File
	| ChatServices.Format2.Elements.Image
	| ChatServices.Format2.Elements.Link
) & { fileBoxID: string };
type TusID = string;

type UseChatFilesParams = {
	isMyRoom: boolean;
	roomID: string;
	type: "Space" | "DM" | "CONFERENCE";
	spaceID?: string;
	myRoomID?: string;
};

type EmoticonType = { text: string; name?: string };
type EmojiType = { name: string; path?: string };
type LocalFile = { file: File; displaySize: string };

export default function useChatFiles(params: UseChatFilesParams) {
	const toast = useToast();
	const stateOnline = useOnlineMonitor();

	const [fileMap, setFileMap] = useState<Record<TusID, ChatFile> | undefined>(undefined);
	const [localFileMap, setLocalFileMap] = useState<LocalFile[]>([]);
	const [fileFormat, setFileFormat] = useState<(ChatFileFormat & { tusID?: string })[] | undefined>(undefined);
	const [emoticon, setEmoticon] = useState<EmoticonType | undefined>(undefined);
	const prevCountFilesRef = useRef<number>((fileMap && Object.keys(fileMap).length) || 0);

	useEffect(() => {
		prevCountFilesRef.current = (fileMap && Object.keys(fileMap).length) || 0;
	}, [fileMap]);

	const tusActions = useUploadTus();

	const { saveLinkBeforeSubmit, uploadFileToChat } = useUploadChat({
		myRoom: params.isMyRoom,
		myRoomID: params.myRoomID,
		roomID: params.roomID,
		spaceID: params.spaceID,
		type: params.type
	});

	const { deleteFile } = useFileManager();

	const getCurrentRichText = useCallback(
		(editor: ChatEditorRefType) => {
			return new Promise<{ richText: ChatServices.Format2.Container.Richtext | null; files?: ChatFileFormat[] }>(
				(resolve) => {
					const contents = editor.getContents(true);
					if (!contents.richText) {
						return resolve({ richText: contents.richText, files: undefined });
					}

					const richTextElements = contents.richText.elements;
					const indexMinElmHasValue = richTextElements.findIndex(
						(elm) => (elm.type === "text" && elm.text) || elm.type !== "text"
					);
					const indexMaxElmHasValue = richTextElements.findLastIndex(
						(elm) => (elm.type === "text" && elm.text) || elm.type !== "text"
					);

					const elements =
						indexMinElmHasValue > -1 && indexMaxElmHasValue > -1
							? richTextElements.slice(indexMinElmHasValue, indexMaxElmHasValue + 1)
							: richTextElements;

					const richText = {
						type: contents.richText.type,
						elements: elements
					};
					const files: ChatFileFormat[] =
						fileFormat?.map((v) => {
							const result = { ...v };
							delete result["tusID"];
							return result;
						}) ?? [];

					if (emoticon) {
						richText?.elements.unshift({ type: "emoticon", ...emoticon });
						const elementsText = richTextElements.filter((item) => item.type === "text");
						if (elementsText.length === 1) {
							const elementText = elementsText[0] as ChatServices.Format2.Elements.Text;
							if (!elementText.text) {
								richText.elements = richTextElements.filter((item) => item.type !== "text");
							}
						}
					}

					if (checkHasLink(richText)) {
						saveLinkBeforeSubmit(richText.elements)
							.then(({ chatForm, fileBoxId }) => {
								if (!richText) return;
								chatForm.forEach((element) => {
									if (element.type === "link" && fileBoxId) {
										files?.push({ ...element, fileBoxID: element.info.fileBoxId });
									}
								});
								return resolve({ richText: { ...richText, elements: chatForm }, files });
							})
							.catch(() => {
								if (!richText) return;
								return resolve({ richText: { ...richText }, files });
							});
					} else {
						return resolve({
							richText,
							files
						});
					}
				}
			);
		},
		[fileFormat, emoticon]
	);

	const startUploadTus = (uploader: TusUpload, tusID: string, file: File) => {
		tusActions.initTusState(tusID, {
			uploader,
			fileMimeType: file.type,
			fileName: file.name,
			status: "pending",
			target: "CHAT",
			percentage: 0
		});
		setFileMap((prev) => {
			const result = { ...prev };
			result[tusID] = {
				file,
				isLoading: true,
				id: tusID,
				tusID,
				displaySize: Utility.formatBytes(file.size)
			};
			return result;
		});
	};

	const afterFileUploadApiSuccess = (
		tusID: string,
		fileFormat: ChatServices.Format2.Elements.File | ChatServices.Format2.Elements.Image
	) => {
		setFileFormat((prev) =>
			prev
				? [...prev, { ...fileFormat, fileBoxID: fileFormat.id, tusID }]
				: [{ ...fileFormat, fileBoxID: fileFormat.id, tusID }]
		);
		setFileMap((prev) => {
			const result = { ...prev };
			result[tusID] = {
				...result[tusID],
				isLoading: false,
				...fileFormat
			};
			return result;
		});
		tusActions.updateUploadedState(tusID, "success");
	};

	const afterFileUploadApiFailed = (tusID: string, file?: File) => {
		tusActions.updateUploadedState(tusID, "error");
		setFileMap((prev) => {
			const result = { ...prev };
			delete result[tusID];
			return result;
		});
		if (file) {
			setLocalFileMap((prev) => [...prev, { file: file, displaySize: Utility.formatBytes(file.size) }]);
		}
	};

	const uploadFileWithTus = useCallback((files: File[] | FileList | null) => {
		if (!files) return;

		const MAX_FILES_UPLOAD = 10;
		if (prevCountFilesRef.current + files.length <= MAX_FILES_UPLOAD) {
			Array.from(files).forEach((file) => {
				const fileName = file.name.substring(0, file.name.lastIndexOf("."));
				if (MessageInValidateFilename(fileName)) {
					toast.show({
						type: "error",
						message: MessageInValidateFilename(fileName)
					});
					return;
				}

				const upload = tusActions.uploadTus(file, {
					onProgress(_, __, percentage) {
						const tusID = upload.url?.split("/upload/").slice(-1)[0];
						if (!tusID) return;
						if (percentage === 0) {
							startUploadTus(upload, tusID, file);
						} else {
							tusActions.updateUploadProgress(tusID, percentage);
						}
					},
					async onSuccess() {
						const tusID = upload.url?.split("/upload/").slice(-1)[0];
						if (!tusID) return;
						try {
							const fileFormat = await uploadFileToChat(tusID, file.name, file);
							if (!fileFormat) return;
							afterFileUploadApiSuccess(tusID, fileFormat);
						} catch (err) {
							const error = err as RestAPIError;
							if (error.code === "46110") {
								toast.show({
									type: "error",
									message: _T("파일명이 200자를 초과했습니다. 파일명을 줄여서 다시 시도해주세요.")
								});
								afterFileUploadApiFailed(tusID);
							} else {
								afterFileUploadApiFailed(tusID, file);
							}
						}
					},
					onError() {
						setLocalFileMap((prev) => [
							...prev,
							{ file: file, displaySize: Utility.formatBytes(file.size) }
						]);
						const tusID = upload.url?.split("/upload/").slice(-1)[0];
						if (!tusID) return;
						tusActions.updateUploadedState(tusID, "error");
						removeFile(tusID);
					}
				});
			});
		} else {
			toast.show({
				type: "error",
				message: _T("첨부파일은 {{max}}개를 초과할 수 없습니다.", { max: MAX_FILES_UPLOAD }),
				closeButton: false
			});
		}
	}, []);

	const removeFile = useCallback((tusID: string, fileID?: string) => {
		setFileMap((prev) => {
			const result = { ...prev };
			delete result[tusID];
			return result;
		});

		setFileFormat((prev) => {
			if (prev?.length === 1) return undefined;
			return prev?.filter((v) => v.tusID !== tusID);
		});

		if (fileID) {
			deleteFile({ id: fileID });
		}
	}, []);

	const removeEmoticon = useCallback(() => {
		setEmoticon(undefined);
	}, []);

	const addEmojiOrEmoticon = useCallback((item: EmojiType, editor: ChatEditorRefType) => {
		if (item.path) {
			setEmoticon({ name: item.name, text: item.path });
		} else {
			const quill = editor.getEditor?.();
			if (!quill) return;
			const selection = quill.getSelection(true);
			quill.insertText(selection.index, item.name);
		}
	}, []);

	//네트워크 상태에 따라 오프라인 -> 온라인이 될때 오프라인시 로컬에만 올려두었던 코드를 TUS를 사용하여 서버로 보내는 코드
	useEffect(() => {
		if (stateOnline && localFileMap) {
			const changeFileType = localFileMap.map((val) => val.file);
			uploadFileWithTus(changeFileType);
			setLocalFileMap([]);
		}
	}, [stateOnline]);

	const clearFiles = () => {
		setFileMap(undefined);
		setFileFormat(undefined);
		setEmoticon(undefined);
	};

	const actions = {
		getCurrentRichText,
		uploadFileWithTus,
		clearFiles,
		removeFile,
		removeEmoticon,
		addEmojiOrEmoticon
	};

	return { fileMap, localFileMap, emoticon, fileFormat, actions };
}
